import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterBar from '../FilterBar';
import ScheduleTable from '../ScheduleTable';
import CartoonLoader from '../CartoonLoader';
import AppPageHeader from '../AppPageHeader';
import { useData } from '../DataContext';
import { comparableScheduleDateKey } from '../sheetDateFormat';

const FILTERS_CACHE_KEY = 'teacher_webapp_filters_cache_v1';
const QUICK_FILTER_CACHE_KEY = 'teacher_webapp_quick_filter_cache_v1';

const defaultLiveFilters = {
  teacher: [],
  subject: [],
  class: [],
  projectName: [],
  year: '',
  startDate: '',
  endDate: ''
};

function readInitialLiveScheduleState() {
  try {
    const cachedFiltersRaw = localStorage.getItem(FILTERS_CACHE_KEY);
    const cachedQuickFilter = localStorage.getItem(QUICK_FILTER_CACHE_KEY);
    if (cachedFiltersRaw) {
      const parsed = JSON.parse(cachedFiltersRaw);
      const normalizedFilters = {
        teacher: Array.isArray(parsed.teacher) ? parsed.teacher : (parsed.teacher ? [parsed.teacher] : []),
        subject: Array.isArray(parsed.subject) ? parsed.subject : (parsed.subject ? [parsed.subject] : []),
        class: Array.isArray(parsed.class) ? parsed.class : (parsed.class ? [parsed.class] : []),
        projectName: Array.isArray(parsed.projectName) ? parsed.projectName : (parsed.projectName ? [parsed.projectName] : []),
        year: parsed.year || '',
        startDate: parsed.startDate || '',
        endDate: parsed.endDate || ''
      };
      if (
        parsed && typeof parsed === 'object' &&
        (normalizedFilters.teacher.length > 0 || normalizedFilters.subject.length > 0 ||
          normalizedFilters.class.length > 0 || normalizedFilters.projectName.length > 0 ||
          normalizedFilters.startDate || normalizedFilters.endDate)
      ) {
        return {
          filters: normalizedFilters,
          activeQuickFilter: cachedQuickFilter || null
        };
      }
    }
  } catch (_) {}

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;
  return {
    filters: { ...defaultLiveFilters, startDate: todayStr, endDate: todayStr },
    activeQuickFilter: 'today'
  };
}

const liveScheduleInitRef = { pair: null };

export default function LiveClassPage() {
  const navigate = useNavigate();
  const { liveClassData, liveClassLoading, liveClassError, refetchLiveClass } = useData();

  const [scheduleData, setScheduleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState(() => {
    liveScheduleInitRef.pair = readInitialLiveScheduleState();
    return liveScheduleInitRef.pair.filters;
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState(() => {
    const pair = liveScheduleInitRef.pair || readInitialLiveScheduleState();
    liveScheduleInitRef.pair = null;
    return pair.activeQuickFilter;
  });

  const handleNavigateToReports = useCallback(() => {
    navigate('/live-class/report');
  }, [navigate]);

  useEffect(() => {
    setScheduleData(liveClassData);
  }, [liveClassData]);

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filters));
      if (activeQuickFilter) {
        localStorage.setItem(QUICK_FILTER_CACHE_KEY, activeQuickFilter);
      } else {
        localStorage.removeItem(QUICK_FILTER_CACHE_KEY);
      }
    } catch (_) {}
  }, [filters, activeQuickFilter]);

  const applyFilters = useCallback(() => {
    let filtered = [...scheduleData];

    if (filters.teacher && filters.teacher.length > 0) {
      filtered = filtered.filter(item => filters.teacher.includes(item.teacher));
    }
    if (filters.subject && filters.subject.length > 0) {
      filtered = filtered.filter(item => filters.subject.includes(item.subject));
    }
    if (filters.class && filters.class.length > 0) {
      filtered = filtered.filter(item => filters.class.includes(item.class));
    }
    if (filters.projectName && filters.projectName.length > 0) {
      filtered = filtered.filter(item => filters.projectName.includes(item.projectName));
    }
    if (filters.year) {
      filtered = filtered.filter(item => {
        const key = comparableScheduleDateKey(item.dateISO);
        if (!key) return false;
        return key.slice(0, 4) === filters.year;
      });
    }
    if (filters.startDate) {
      filtered = filtered.filter(item => {
        const key = comparableScheduleDateKey(item.dateISO);
        return key && key >= filters.startDate;
      });
    }
    if (filters.endDate) {
      filtered = filtered.filter(item => {
        const key = comparableScheduleDateKey(item.dateISO);
        return key && key <= filters.endDate;
      });
    }

    filtered.sort((a, b) => new Date(a.dateISO) - new Date(b.dateISO));
    setFilteredData(filtered);
  }, [scheduleData, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    if (filterName === 'startDate' || filterName === 'endDate') {
      setActiveQuickFilter(null);
    }
  };

  /** Single update for month/week/year quick filters so start/end + quick state stay in sync. */
  const handleDateRangeCommit = useCallback(({ startDate, endDate, activeQuickFilter: qf }) => {
    setFilters(prev => ({ ...prev, startDate, endDate }));
    setActiveQuickFilter(qf ?? null);
  }, []);

  const handleReset = () => {
    setFilters({
      teacher: [],
      subject: [],
      class: [],
      projectName: [],
      year: '',
      startDate: '',
      endDate: ''
    });
    setActiveQuickFilter(null);
    try {
      localStorage.removeItem(FILTERS_CACHE_KEY);
      localStorage.removeItem(QUICK_FILTER_CACHE_KEY);
    } catch (_) {}
  };

  const handleExport = () => {
    const csvContent = [
      ['Date', 'Day', 'Time', 'Subject', 'Teacher', 'Class'],
      ...filteredData.map(item => [item.date, item.day, item.time, item.subject, item.teacher, item.class])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'schedule_export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const allUniqueClasses = Array.from(new Set(scheduleData.map(row => row.class).filter(Boolean)));

  if (liveClassLoading) {
    return <CartoonLoader message="Please wait until the classes are caught..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppPageHeader
        onBack={() => navigate('/')}
        title="AFS Live Class Schedule"
        rightSlot={
          <button
            type="button"
            onClick={handleNavigateToReports}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Reports
          </button>
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {liveClassError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
                <p className="text-sm text-red-700 mt-1">{liveClassError}</p>
              </div>
            </div>
          </div>
        )}

        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onDateRangeCommit={handleDateRangeCommit}
          onReset={handleReset}
          onExport={handleExport}
          onRefetch={refetchLiveClass}
          data={scheduleData}
          filteredData={filteredData}
          activeQuickFilter={activeQuickFilter}
          setActiveQuickFilter={setActiveQuickFilter}
        />

        <ScheduleTable data={filteredData} allUniqueClasses={allUniqueClasses} />
      </main>
    </div>
  );
}
